import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, ArrowUpDown, MapPin, Zap } from './Icons';
import GateMapPicker from './GateMapPicker';
import { triggerCrossing } from '../services/api';
import './DeviceSettings.css';

// Helper to parse Firebase timestamps (outside component to avoid re-creation)
const parseFirebaseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  // Handle Firestore timestamp object with _seconds
  if (timestamp._seconds !== undefined) {
    return new Date(timestamp._seconds * 1000);
  }
  // Handle ISO string
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }
  // Handle seconds as number
  if (typeof timestamp === 'number') {
    return new Date(timestamp * 1000);
  }
  return null;
};

const DeviceSettings = ({ config, allGates, gateAssignments, locations, currentDeviceId, onSave, saving, onGateCoordinatesUpdated }) => {
  // Local state for form fields - only the commonly adjusted ones
  const [clipDuration, setClipDuration] = useState('');
  const [zoomLevel, setZoomLevel] = useState('');
  const [timestampCorrection, setTimestampCorrection] = useState('');
  const [segmentDuration, setSegmentDuration] = useState('');
  const [selectedGates, setSelectedGates] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showGatePicker, setShowGatePicker] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [gateSortField, setGateSortField] = useState('createdAt');
  const [gateSortDesc, setGateSortDesc] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState(null);

  // Initialize form values when config loads
  useEffect(() => {
    if (config) {
      const cfg = config.config || {};
      setClipDuration(cfg.CLIP_EXTRACTION_DURATION_MS?.toString() || '6000');
      setZoomLevel(cfg.DEFAULT_ZOOM_LEVEL?.toString() || '1');
      setTimestampCorrection(cfg.TIMESTAMP_CORRECTION_MS?.toString() || '0');
      setSegmentDuration(cfg.VIDEO_SEGMENT_DURATION_MS?.toString() || '60000');
      setSelectedGates(config.gate_ids || []);
      setLocationId(config.location_id || '');
      setHasChanges(false);
    }
  }, [config]);

  // Track changes
  const handleFieldChange = (setter) => (e) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  // Get available gates (not already selected) with sorting
  const availableGates = useMemo(() => {
    const gates = (allGates || [])
      .filter(gate => !selectedGates.includes(gate.gateId))
      .map(gate => ({
        ...gate,
        userPrefix: gate.userId ? gate.userId.substring(0, 5) : '—',
        locationName: gate.location_name || '—',
        trailPlacement: [gate.trail_name, gate.placement].filter(Boolean).join(': ') || '—',
        createdAtDate: parseFirebaseTimestamp(gate.createdAt)
      }));

    // Sort gates
    gates.sort((a, b) => {
      let comparison = 0;
      if (gateSortField === 'createdAt') {
        const aTime = a.createdAtDate?.getTime() || 0;
        const bTime = b.createdAtDate?.getTime() || 0;
        comparison = aTime - bTime;
      } else if (gateSortField === 'location') {
        comparison = (a.locationName || '').localeCompare(b.locationName || '');
      } else if (gateSortField === 'trailPlacement') {
        comparison = (a.trailPlacement || '').localeCompare(b.trailPlacement || '');
      }
      return gateSortDesc ? -comparison : comparison;
    });
    
    return gates;
  }, [allGates, selectedGates, gateSortField, gateSortDesc]);

  // Get selected gate details
  const selectedGateDetails = useMemo(() => {
    return selectedGates.map(gateId => {
      const gate = (allGates || []).find(g => g.gateId === gateId);
      return {
        gateId,
        userPrefix: gate?.userId ? gate.userId.substring(0, 5) : '—',
        createdAtDate: gate?.createdAt ? parseFirebaseTimestamp(gate.createdAt) : null
      };
    });
  }, [selectedGates, allGates]);

  // Gate management
  const handleAddGate = (gateId) => {
    if (gateId && !selectedGates.includes(gateId)) {
      setSelectedGates([...selectedGates, gateId]);
      setHasChanges(true);
    }
  };

  const handleRemoveGate = (gateId) => {
    setSelectedGates(selectedGates.filter(g => g !== gateId));
    setHasChanges(true);
  };

  const handleToggleSort = (field) => {
    if (gateSortField === field) {
      setGateSortDesc(!gateSortDesc);
    } else {
      setGateSortField(field);
      setGateSortDesc(true);
    }
  };

  // Save changes
  const handleSave = () => {
    const updates = {
      config: {
        CLIP_EXTRACTION_DURATION_MS: parseInt(clipDuration) || 6000,
        DEFAULT_ZOOM_LEVEL: parseFloat(zoomLevel) || 1,
        TIMESTAMP_CORRECTION_MS: parseInt(timestampCorrection) || 0,
        VIDEO_SEGMENT_DURATION_MS: parseInt(segmentDuration) || 60000
      },
      gate_ids: selectedGates,
      location_id: locationId || null
    };
    onSave(updates);
  };

  // Reset to original values
  const handleReset = () => {
    if (config) {
      const cfg = config.config || {};
      setClipDuration(cfg.CLIP_EXTRACTION_DURATION_MS?.toString() || '6000');
      setZoomLevel(cfg.DEFAULT_ZOOM_LEVEL?.toString() || '1');
      setTimestampCorrection(cfg.TIMESTAMP_CORRECTION_MS?.toString() || '0');
      setSegmentDuration(cfg.VIDEO_SEGMENT_DURATION_MS?.toString() || '60000');
      setSelectedGates(config.gate_ids || []);
      setLocationId(config.location_id || '');
      setHasChanges(false);
    }
  };

  // Handle trigger button click
  const handleTrigger = async () => {
    setTriggerLoading(true);
    setTriggerMessage(null);
    try {
      await triggerCrossing(currentDeviceId);
      setTriggerMessage({ type: 'success', text: 'Trigger sent successfully' });
      // Clear message after 3 seconds
      setTimeout(() => setTriggerMessage(null), 3000);
    } catch (error) {
      setTriggerMessage({ type: 'error', text: 'Failed to send trigger' });
      setTimeout(() => setTriggerMessage(null), 5000);
    } finally {
      setTriggerLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '—';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!config) {
    return (
      <div className="device-settings">
        <div className="empty-state">
          <p>No configuration data available.</p>
        </div>
      </div>
    );
  }

  const cfg = config.config || {};

  return (
    <div className="device-settings">
      {/* <div className="settings-header">
        <h3>
          <Settings size={18} />
          Device Configuration
        </h3>
        {hasChanges && (
          <span className="unsaved-indicator">Unsaved changes</span>
        )}
      </div> */}

      {/* Quick Settings - Most commonly adjusted */}
      <div className="settings-section">
        {/* <h4>Quick Settings</h4> */}
        <div className="settings-grid">
          <div className="setting-field">
            <label htmlFor="clipDuration">
              Clip Extraction Duration
              <span className="field-hint">How long each extracted clip is (ms)</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                id="clipDuration"
                value={clipDuration}
                onChange={handleFieldChange(setClipDuration)}
                min="1000"
                max="60000"
                step="1000"
              />
              <span className="unit">ms</span>
            </div>
            <span className="current-value">{(parseInt(clipDuration) / 1000).toFixed(1)}s</span>
          </div>

          <div className="setting-field">
            <label htmlFor="zoomLevel">
              Default Zoom Level
              <span className="field-hint">Camera zoom multiplier</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                id="zoomLevel"
                value={zoomLevel}
                onChange={handleFieldChange(setZoomLevel)}
                min="0.6"
                max="10"
                step="0.1"
              />
              <span className="unit">x</span>
            </div>
          </div>

          <div className="setting-field">
            <label htmlFor="timestampCorrection">
              Timestamp Correction
              <span className="field-hint">Offset applied to timestamps (ms)</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                id="timestampCorrection"
                value={timestampCorrection}
                onChange={handleFieldChange(setTimestampCorrection)}
                step="100"
              />
              <span className="unit">ms</span>
            </div>
            <span className="current-value">{(parseInt(timestampCorrection) / 1000).toFixed(2)}s</span>
          </div>

          <div className="setting-field">
            <label htmlFor="segmentDuration">
              Video Segment Duration
              <span className="field-hint">Length of each recorded segment (ms)</span>
            </label>
            <div className="input-with-unit">
              <input
                type="number"
                id="segmentDuration"
                value={segmentDuration}
                onChange={handleFieldChange(setSegmentDuration)}
                min="10000"
                max="300000"
                step="10000"
              />
              <span className="unit">ms</span>
            </div>
            <span className="current-value">{(parseInt(segmentDuration) / 1000 / 60).toFixed(1)} min</span>
          </div>
        </div>
      </div>

      {/* Trigger Section */}
      <div className="settings-section trigger-section">
        <div className="trigger-content">
          {/* <h4 style={{ margin: 0 }}>Manual Trigger</h4> */}

          <button 
            className={`trigger-btn ${triggerLoading ? 'loading' : ''}`}
            onClick={handleTrigger}
            disabled={triggerLoading}
          >
            <Zap size={16} />
            {triggerLoading ? 'Sending...' : 'Trigger Camera'}
          </button>

          {triggerMessage && (
            <span className={`trigger-message ${triggerMessage.type}`}>
              {triggerMessage.text}
            </span>
          )}
        </div>
      </div>


      {/* Location Assignment */}
      <div className="settings-section">
        <h4>Location Assignment</h4>
        <div className="setting-field location-field">
          <label htmlFor="locationId">
            Assigned Location
            <span className="field-hint">Links this device to a location for filtering</span>
          </label>
          <select
            id="locationId"
            className="location-select"
            value={locationId}
            onChange={(e) => { setLocationId(e.target.value); setHasChanges(true); }}
          >
            <option value="">— No location assigned —</option>
            {(locations || []).map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>
          {locationId && (
            <span className="current-value location-id-hint">ID: {locationId}</span>
          )}
        </div>
      </div>

      {/* Gate Assignments */}
      <div className="settings-section">
        <h4>Gate Assignments</h4>
        
        {/* Currently assigned gates */}
        <div className="assigned-gates">
          <div className="gates-table-header">
            <span className="col-gate">Assigned Gates ({selectedGates.length})</span>
            <span className="col-user">User</span>
            <span className="col-actions"></span>
          </div>
          {selectedGateDetails.length === 0 ? (
            <div className="no-gates">No gates assigned to this device</div>
          ) : (
            <div className="gates-table-body">
              {selectedGateDetails.map((gate) => (
                <div key={gate.gateId} className="gate-row assigned">
                  <span className="col-gate">
                    <MapPin size={14} />
                    {gate.gateId}
                  </span>
                  <span className="col-user">{gate.userPrefix}</span>
                  <span className="col-actions">
                    <button 
                      className="remove-gate-btn"
                      onClick={() => handleRemoveGate(gate.gateId)}
                      title="Remove gate"
                    >
                      <X size={14} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add gate buttons */}
        <div className="add-gate-actions">
          <button 
            className="add-gate-btn"
            onClick={() => setShowGatePicker(!showGatePicker)}
          >
            <Plus size={14} />
            Add from List
          </button>
          <button 
            className="add-gate-btn map-btn"
            onClick={() => setShowMapPicker(true)}
          >
            <MapPin size={14} />
            Select from Map
          </button>
        </div>

        {/* Gate picker table */}
        {showGatePicker && (
          <div className="gate-picker">
            <div className="gate-picker-header">
              <span>Available Gates ({availableGates.length})</span>
              <button 
                className="close-picker"
                onClick={() => setShowGatePicker(false)}
              >
                <X size={14} />
              </button>
            </div>
            
            {/* Sort controls */}
            <div className="gates-table-header sortable">
              <button
                className={`col-location sort-btn ${gateSortField === 'location' ? 'active' : ''}`}
                onClick={() => handleToggleSort('location')}
              >
                Location
                <ArrowUpDown size={12} />
              </button>
              <button
                className={`col-trail sort-btn ${gateSortField === 'trailPlacement' ? 'active' : ''}`}
                onClick={() => handleToggleSort('trailPlacement')}
              >
                Trail: Placement
                <ArrowUpDown size={12} />
              </button>
              <button 
                className={`col-created sort-btn ${gateSortField === 'createdAt' ? 'active' : ''}`}
                onClick={() => handleToggleSort('createdAt')}
              >
                Created
                <ArrowUpDown size={12} />
              </button>
              <span className="col-actions"></span>
            </div>
            
            <div className="gates-table-body picker">
              {availableGates.length === 0 ? (
                <div className="no-gates">All gates are already assigned</div>
              ) : (
                availableGates.map((gate) => (
                  <div key={gate.gateId} className="gate-row">
                    <span className="col-location" title={gate.gateId}>
                      <MapPin size={14} />
                      {gate.locationName}
                    </span>
                    <span className="col-trail">{gate.trailPlacement}</span>
                    <span className="col-created">{formatDate(gate.createdAtDate)}</span>
                    <span className="col-actions">
                      <button 
                        className="add-gate-row-btn"
                        onClick={() => handleAddGate(gate.gateId)}
                        title="Add gate"
                      >
                        <Plus size={14} />
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <GateMapPicker
          gates={allGates}
          selectedGates={selectedGates}
          gateAssignments={gateAssignments || {}}
          currentDeviceId={currentDeviceId}
          onSelectGate={handleAddGate}
          onClose={() => setShowMapPicker(false)}
          onGateCoordinatesUpdated={onGateCoordinatesUpdated}
        />
      )}

      {/* Advanced Settings Toggle */}
      <div className="settings-section">
        <button 
          className="toggle-advanced"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? '▼ Hide' : '► Show'} All Configuration
        </button>
        
        {showAdvanced && (
          <div className="advanced-config">
            <div className="config-grid">
              {Object.entries(cfg).map(([key, value]) => (
                <div key={key} className="config-item">
                  <span className="config-key">{key}</span>
                  <span className="config-value">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Device Info */}
      <div className="settings-section device-info">
        <h4>Device Info</h4>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Status</span>
            <span className={`info-value status-${config.status}`}>{config.status}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Created</span>
            <span className="info-value">
              {config.device_created_at ? new Date(config.device_created_at).toLocaleString() : 'Unknown'}
            </span>
          </div>
          {config.last_restart_time && (
            <>
              <div className="info-item">
                <span className="info-label">Last Restart</span>
                <span className="info-value">{new Date(config.last_restart_time).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Restart Reason</span>
                <span className="info-value restart-reason">{config.last_restart_reason}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="settings-actions">
        <button 
          className="reset-btn"
          onClick={handleReset}
          disabled={!hasChanges || saving}
        >
          Reset
        </button>
        <button 
          className="save-btn"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default DeviceSettings;
